import { STATUS, INK, BRAND, OPERATION } from 'Utils/styleTokens';

// Risk → color maps, sourced from the style-guide STATUS triads:
// critical → danger, high → warning, medium → neutral (softer step — warning is
// taken by high), low → success, protected → success (darker text step so flow
// lines stay distinguishable from low), unknown → neutral.
export const RISK_COLORS = {
  critical: STATUS.danger.dot,
  high:     STATUS.warning.dot,
  medium:   STATUS.neutral.dot,
  low:      STATUS.success.dot,
  protected: STATUS.success.text,
  unknown:  INK[300],
};

export const RISK_TEXT_COLORS = {
  critical: STATUS.danger.text,
  high:     STATUS.warning.text,
  medium:   STATUS.neutral.text,
  low:      STATUS.success.text,
  protected: STATUS.success.text,
  unknown:  STATUS.neutral.text,
};

export const RISK_BG_COLORS = {
  critical: STATUS.danger.bg,
  high:     STATUS.warning.bg,
  medium:   STATUS.neutral.bg,
  low:      STATUS.success.bg,
  protected: STATUS.success.bg,
  unknown:  STATUS.neutral.bg,
};

export const TYPE_COLORS = {
  pg:    { text: STATUS.info.text, bg: STATUS.info.bg },
  mysql: { text: STATUS.success.text, bg: STATUS.success.bg },
  mongo: { text: BRAND.violet, bg: OPERATION.mask.bg },
  csv:   { text: OPERATION.synthesize.text, bg: OPERATION.synthesize.bg },
  nosql: { text: BRAND.violet, bg: OPERATION.mask.bg },
  file:  { text: OPERATION.synthesize.text, bg: OPERATION.synthesize.bg },
};

export const TYPE_LABELS = {
  pg: 'PG',
  mysql: 'MySQL',
  mongo: 'Mongo',
  csv: 'CSV',
  nosql: 'NoSQL',
  file: 'File',
};

export const NODE_WIDTH = 132;
export const NODE_HEIGHT = 88;
export const SVG_WIDTH = 540;
export const SVG_HEIGHT = 310;

const RISK_SCORE = { critical: 4, high: 3, medium: 2, low: 1, protected: 0 };

export function lineColor(riskA, riskB) {
  const a = RISK_SCORE[riskA] != null ? RISK_SCORE[riskA] : 1;
  const b = RISK_SCORE[riskB] != null ? RISK_SCORE[riskB] : 1;
  const hi = Math.max(a, b);
  const lo = Math.min(a, b);
  if (hi >= 3 && lo === 0) return STATUS.success.text;
  if (hi >= 4)             return STATUS.danger.dot;
  if (hi >= 3)             return STATUS.warning.dot;
  if (hi >= 2)             return STATUS.neutral.dot;
  return STATUS.success.dot;
}

export function lineDesc(riskA, riskB) {
  const c = lineColor(riskA, riskB);
  if (c === STATUS.success.text) return 'remediated copy';
  if (c === STATUS.danger.dot) return 'critical risk';
  if (c === STATUS.warning.dot) return 'high risk';
  if (c === STATUS.neutral.dot) return 'medium risk';
  return 'clean';
}

export function datePrecedence(s) {
  if (!s || ['Monthly', 'Daily', 'Weekly'].includes(s)) return 9e15;
  const d = new Date(s);
  return isNaN(d.getTime()) ? 9e15 : d.getTime();
}

export function rectEdge(cx, cy, hw, hh, tx, ty) {
  const dx = tx - cx;
  const dy = ty - cy;
  if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) return { x: cx, y: cy };
  const sx = Math.abs(dx) > 0.01 ? hw / Math.abs(dx) : Infinity;
  const sy = Math.abs(dy) > 0.01 ? hh / Math.abs(dy) : Infinity;
  const t = Math.min(sx, sy);
  return { x: cx + dx * t, y: cy + dy * t };
}

const RISK_ORDER = { critical: 0, high: 1, medium: 2, low: 3, protected: 4, unknown: 5 };

/**
 * Milliseconds of an identity's most recent profile snapshot, or 0 when it has never been
 * profiled. 0 (not Infinity) so never-profiled identities sort to the BOTTOM of a
 * newest-first list rather than the top — "no data yet" is not "freshest".
 */
export function lastProfiledMs(identity) {
  const t = identity && identity.latestSnapshotAt;
  if (!t) return 0;
  const ms = Date.parse(t);
  return Number.isNaN(ms) ? 0 : ms;
}

export function sortIdentities(items, sortKey) {
  const d = [...items];
  if (sortKey === 'size')  return d.sort((a, b) => b.sizeGB - a.sizeGB);
  if (sortKey === 'width') return d.sort((a, b) => b.tables - a.tables);
  if (sortKey === 'depth') return d.sort((a, b) => b.rowsM - a.rowsM);
  if (sortKey === 'age')   return d.sort((a, b) => datePrecedence(a.origin) - datePrecedence(b.origin));
  // Newest profile first. The default: after running something, the expectation is to find
  // it at the top, and risk order buried a just-profiled source wherever its risk tier fell.
  // Ties break on name so repeated renders are stable rather than arbitrary.
  if (sortKey === 'recent') {
    return d.sort((a, b) =>
      lastProfiledMs(b) - lastProfiledMs(a)
      || String(a.name || '').localeCompare(String(b.name || '')));
  }
  return d.sort((a, b) => (RISK_ORDER[a.risk] ?? 99) - (RISK_ORDER[b.risk] ?? 99));
}
